import { useRef, useCallback, useEffect } from 'react';
import type { Config, OnEventProcessed, SingleEvent, EventForQueueHandler } from './types.tsx';
import { useRealtimeQueueContext } from './utils/context/index.tsx';

export const useAreRealtimeQueueQuotasExceeded = () => {
	const { areQueueQuotasExceeded, eventsInQueue } = useRealtimeQueueContext();

	return { areQueueQuotasExceeded, eventsInQueue };
};

// TODO: Drop after full realtime migration jpd_issue_subscription_migration
export const useWrappedRealtimeHandlerWithQueue = <PAYLOAD, TYPES>(
	handler: OnEventProcessed,
	config?: Config,
	shouldSkipEvent?: (event: SingleEvent<PAYLOAD, TYPES>) => boolean,
): [EventForQueueHandler<PAYLOAD, TYPES>, () => void] => {
	const { queue } = useRealtimeQueueContext();
	const ref = useRef<OnEventProcessed | null>(null);

	useEffect(() => {
		ref.current = handler;
		// set queue config
		config && queue.setConfig(config);
	}, [ref, handler, config, queue]);

	const onReceive = useCallback(
		(event: SingleEvent<PAYLOAD, TYPES>) => {
			if (shouldSkipEvent && shouldSkipEvent(event)) {
				return;
			}

			ref.current && queue.addEvent(ref, event);
		},
		[queue, ref, shouldSkipEvent],
	);

	const clearEvents = useCallback(() => {
		ref.current && queue.clearEvents(ref);
	}, [queue]);

	return [onReceive, clearEvents];
};
